import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Search, Music4 } from "lucide-react";
import { ORCHESTRATION_RESOURCES, ORCHESTRATION_GROUPS } from "../data/orchestrationResources";

function ResourceTile({ r }) {
  return (
    <a
      href={r.website}
      target="_blank"
      rel="noopener noreferrer"
      className="group glass-card p-6 flex flex-col items-center text-center hover:border-[#7DA3D9]/60 transition-all"
      title={`Visit ${r.name}`}
      data-testid={`orch-${r.domain}`}
    >
      <div className="w-20 h-20 rounded-md bg-white p-3 border border-white/10 group-hover:border-[#7DA3D9] group-hover:scale-105 transition-all shadow-lg mb-4">
        <img
          src={`https://logo.clearbit.com/${r.domain}`}
          alt={`${r.name} logo`}
          className="w-full h-full object-contain"
          onError={(e) => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = `https://www.google.com/s2/favicons?domain=${r.domain}&sz=128`;
          }}
        />
      </div>
      <div className="font-display text-base group-hover:text-[#B8D0EE] transition-colors leading-tight">
        {r.name}
      </div>
      <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 mt-2 inline-flex items-center gap-1">
        Visit <ExternalLink className="w-3 h-3" />
      </div>
    </a>
  );
}

export default function OrchestrationPage() {
  const [q, setQ] = useState("");

  const grouped = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return ORCHESTRATION_GROUPS.map((g) => ({
      ...g,
      items: ORCHESTRATION_RESOURCES.filter(
        (r) => r.category === g.key && (!needle || r.name.toLowerCase().includes(needle)),
      ),
    })).filter((g) => g.items.length > 0);
  }, [q]);

  return (
    <div className="min-h-screen bg-[#0A1128] pt-28 pb-20" data-testid="orchestration-page">
      <div className="max-w-7xl mx-auto px-6">
        <nav className="text-xs text-slate-500 flex items-center gap-2 mb-6">
          <Link to="/" className="hover:text-white transition-colors">Home</Link>
          <span>/</span>
          <span className="text-[#B8D0EE]">Orchestration</span>
        </nav>

        <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
          <div className="lg:col-span-8">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-[#7DA3D9]/25 bg-[#7DA3D9]/5 mb-6">
              <Music4 className="w-3.5 h-3.5 text-[#7DA3D9]" />
              <span className="text-[10px] uppercase tracking-[0.25em] text-[#B8D0EE] font-semibold">
                Orchestration · Scoring · Arrangement
              </span>
            </div>
            <h1 className="font-display text-4xl lg:text-7xl tracking-tight leading-[0.98]" data-testid="orch-title">
              Writing for <span className="text-[#7DA3D9]">orchestra</span>.
            </h1>
            <div className="overline mt-4">{ORCHESTRATION_RESOURCES.length} resources indexed</div>
          </div>
          <div className="lg:col-span-4">
            <p className="text-slate-300 leading-relaxed text-lg">
              Every composer's orchestration toolkit — sample libraries, notation software,
              live orchestras, recording orchestras, scoring stages, education programs, and
              the trade associations advocating for orchestrators.
            </p>
          </div>
        </div>

        <div className="glass-card p-5 mb-10 relative">
          <Search className="w-4 h-4 absolute left-9 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            className="input-dark pl-10"
            placeholder="Search orchestration resources…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="orch-search"
          />
        </div>

        {grouped.length === 0 ? (
          <div className="text-center py-16 text-slate-400">No resources match your search.</div>
        ) : (
          <div className="space-y-14">
            {grouped.map((g) => (
              <section key={g.key} data-testid={`orch-group-${g.key.replace(/[^a-z]+/gi, "-").toLowerCase()}`}>
                <div className="overline">{g.overline}</div>
                <h2 className="font-display text-2xl lg:text-4xl mb-6 mt-2">{g.label}</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {g.items.map((r) => (
                    <ResourceTile key={r.website} r={r} />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
